import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CollectionStatus } from '../../../data-layer/collections/types';
import { COLLECTIONS_NAMESPACE_PREFIX } from '../../../data-layer/posts/reducer';
import { toPostUI } from '../../../mappers/posts/toPostUI';
import { CollectionEmpty } from '../../../ui/collection/CollectionEmpty';
import { CollectionInfo } from '../../../ui/collection/CollectionInfo';
import { CollectionLoading } from '../../../ui/collection/CollectionLoading';
import { CollectionNotFound } from '../../../ui/collection/CollectionNotFound';
import { CollectionScrollableHeader } from '../../../ui/collection/CollectionScrollableHeader';
import { CollectionStickyHeader } from '../../../ui/collection/CollectionStickyHeader';
import { StickyFooter } from '../../../ui/collection/StickyFooter';
import { MOBILE_FOOTER_HEIGHT } from '../../../ui/collection/constants';
import { useHeaderIntersection } from '../../../ui/collection/hooks/useHeaderIntersection';
import { useHeaderSizes } from '../../../ui/collection/hooks/useHeaderSizes';
import { getMetaInfo } from '../../../ui/collection/utils/getMetaInfo';
import { DashboardPage } from '../../../ui/dashboard/DashboardPage';
import { Box } from '../../../ui/layout/Box';
import { ResizableGrid } from '../../../ui/layout/ResizableGrid/ResizableGrid';
import { PostCardGrid } from '../../../ui/posts/PostCardGrid/PostCardGrid';
import { Typography } from '../../../ui/typography/Typography';
import { CollectionMenu } from '../../../ui/users/CollectionMenu';
import { useEventSource } from '../../../utils/LocationContext';
import { analyticsService, EventSource } from '../../../utils/analyticsService';
import { getIsMobile } from '../../../utils/getIsMobile';
import { ymTrackOpenCollectionLink, ymTrackPostInGridClick } from '../../../utils/metrika';
import { useOnCollectionOpen } from '../../../utils/useIsCollectionPromoteInFeed';
import { useMeta } from '../../../utils/useMeta';
import { useProductsPreviewEnabled } from '../../../utils/useProductsPreviewEnabled';
import { usePostCollection } from '../../post/usePostCollection';
import { withPostCallbacks } from '../../post/withPostCallbacks';
import { useResetScroll } from '../../search/hooks/useResetScroll';
import { useCanonicalUrl } from '../../utils/useCanonicalUrl';
import { useCollection } from '../useCollection';
import { useCurrentUser } from '../useCurrentUser';
import { withCollectionCallbacks } from '../withCollectionCallbacks';
import { PER_PAGE } from './constants';
import { MainContainer, ScrollableContainer } from './styles';
import { buildMeta } from './utils/buildMeta';
import { getItemHeightMiddleware } from './utils/getItemHeight';

const EnhancedStickyFooter = withCollectionCallbacks(StickyFooter);
const EnchancedPostGridCardCompact = withPostCallbacks(PostCardGrid, EventSource.COLLECTION);
const EnhancedCollectionMenu = withCollectionCallbacks(CollectionMenu);

export const CollectionDetails = () => {
    const params = useParams();
    const currentUser = useCurrentUser();
    const collectionId = String(parseInt(params.collectionId!, 10));
    const navigate = useNavigate();
    const { eventSource, prevEventSource } = useEventSource();
    const { collectionModel, loadError, isLoading } = useCollection({ collectionId });
    const collection = usePostCollection({
        namespace: `${COLLECTIONS_NAMESPACE_PREFIX}${collectionId}`,
        filters: { collectionId, amount: PER_PAGE },
        forceFiltersUpdate: true,
    });
    const { isMobile } = getIsMobile();
    const onCollectionOpen = useOnCollectionOpen();
    const gridContainerRef = useRef<HTMLDivElement>(null);
    const productsPreviewEnabled = useProductsPreviewEnabled();

    const hideFooter =
        collectionModel?.defaultCollection ||
        (!collectionModel?.modifiable &&
            (!collectionModel?.isPublic || collectionModel.status !== CollectionStatus.PUBLISHED));

    const { targetRef, isIntersectContent } = useHeaderIntersection({
        containerRef: gridContainerRef,
    });
    const { headerHeight } = useHeaderSizes();

    const { onScroll } = useResetScroll(
        gridContainerRef,
        `${collection.items[0]?.id}-collection-post-grid`,
        !isLoading && collection.items.length > 0,
    );

    const canonicalUrl = useCanonicalUrl();
    useMeta(buildMeta(collectionModel, canonicalUrl));

    const onPostCardClick = useCallback(
        ({ postNumber }: { postNumber: number }) => {
            const {
                id,
                url: postUrl,
                logContext,
                djRequestId,
            } = toPostUI(collection.items[postNumber]);
            ymTrackPostInGridClick({
                page: 'Коллекция',
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
            });
            analyticsService.trackGridTap({
                eventSource: EventSource.COLLECTION,
                postId: id,
                postNumber,
                collectionId: collectionId,
                logContext,
                djRequestId,
            });
            navigate(postUrl, {
                state: {
                    fromCollectionId: collectionId,
                },
            });
        },
        [
            collection.items,
            collectionId,
            currentUser?.login,
            currentUser?.profile?.publicId,
            navigate,
        ],
    );

    useEffect(() => collection.onReset, [collection.onReset]);

    useEffect(() => {
        if (collectionModel?.id && !prevEventSource && eventSource === EventSource.COLLECTION) {
            ymTrackOpenCollectionLink({
                page: 'Коллекция',
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
                isPublic: collectionModel?.isPublic,
                own: collectionModel?.modifiable,
                collectionId: collectionModel?.id,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collectionModel?.id, eventSource, prevEventSource]);

    useEffect(() => {
        if (collectionModel?.id) {
            onCollectionOpen?.(collectionModel);

            analyticsService.trackCollectionOpened({
                collectionId: collectionModel.id,
                logContext: collectionModel.logContext,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collectionModel?.id]);

    const renderItem = useCallback(
        (index: number, columns: number, isVisible?: boolean) => {
            const postModel = collection.items[index];

            if (!postModel) {
                return null;
            }

            return (
                <EnchancedPostGridCardCompact
                    post={postModel}
                    onClick={onPostCardClick}
                    index={index}
                    gridColumns={columns}
                    isVisible={isVisible}
                    hideReportedPosts={false}
                    withProductsPreview={productsPreviewEnabled}
                />
            );
        },
        [collection.items, onPostCardClick, productsPreviewEnabled],
    );

    const menuItem = useMemo(
        () =>
            collectionModel && !collectionModel.defaultCollection ? (
                <Box ml={0.5}>
                    <EnhancedCollectionMenu collection={collectionModel} triggerSize="xs" />
                </Box>
            ) : null,
        [collectionModel],
    );

    if (isLoading === null || isLoading === true) {
        return <CollectionLoading />;
    }

    if (loadError) {
        return <CollectionNotFound />;
    }

    return (
        <DashboardPage height={{ xs: '100dvh', sm: '100vh' }} gridFeed hideHeader>
            <MainContainer>
                <ScrollableContainer ref={gridContainerRef} onScroll={onScroll}>
                    <CollectionStickyHeader
                        collectionModel={collectionModel}
                        showAuthorContent={isIntersectContent}
                        collectionInfo={
                            !isMobile && (
                                <CollectionInfo
                                    collectionModel={collectionModel}
                                    menuItem={menuItem}
                                />
                            )
                        }
                    />
                    <Box
                        width="100%"
                        height={`calc(100% - ${headerHeight}px - ${
                            hideFooter ? 0 : MOBILE_FOOTER_HEIGHT
                        }px)`}
                        px={{ xs: 1, sm: 2 }}
                        mt={headerHeight / 8}
                    >
                        <CollectionScrollableHeader
                            collectionModel={collectionModel}
                            targetRef={targetRef}
                            collectionInfo={
                                isMobile ? (
                                    <CollectionInfo collectionModel={collectionModel} />
                                ) : null
                            }
                        />
                        {collectionModel?.modifiable && !collectionModel.defaultCollection ? (
                            <Typography px={{ xs: 1, sm: 0 }} pb={isMobile ? 2 : 1}>
                                {getMetaInfo(collectionModel)}
                            </Typography>
                        ) : null}
                        {collectionModel && collectionModel.previewItems.length === 0 ? (
                            <CollectionEmpty />
                        ) : (
                            <ResizableGrid
                                key={`${collection.items[0]?.id}-collection-post-grid`}
                                bottomYMargin={hideFooter ? undefined : MOBILE_FOOTER_HEIGHT}
                                itemsXMargin={4}
                                topYMargin={isMobile ? 8 : 16}
                                containerRef={gridContainerRef}
                                items={collection.items}
                                loadMore={collection.onLoadNext}
                                getItemHeight={getItemHeightMiddleware(
                                    collection,
                                    productsPreviewEnabled,
                                )}
                                renderItem={renderItem}
                            />
                        )}
                    </Box>
                </ScrollableContainer>
                {!hideFooter && <EnhancedStickyFooter collection={collectionModel} />}
            </MainContainer>
        </DashboardPage>
    );
};
